import Battery0BarIcon from '@mui/icons-material/Battery0Bar';
import Battery2BarIcon from '@mui/icons-material/Battery2Bar';
import Battery4BarIcon from '@mui/icons-material/Battery4Bar';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryUnknownIcon from '@mui/icons-material/BatteryUnknown';
import { SvgIcon } from '@mui/material';
import { find, sortBy } from 'lodash';
import mixpanel from 'mixpanel-browser';

import { DeviceHullType } from '@/api/devices';
import { ScoutLatestData, ScoutLatestDataWaypoint } from '@/api/scout';

export const isScoutDevice = (hullType?: DeviceHullType | null) => hullType === 'scout';

export const MAX_SCOUT_BATTERY_LEVEL = 4;

const BATTERY_STATUSES = [
    { max: 20, level: 1, StatusIcon: Battery0BarIcon, color: 'error', description: 'Battery is nearly empty' },
    { max: 60, level: 2, StatusIcon: Battery2BarIcon, color: 'warning', description: 'Battery is low' },
    { max: 89, level: 3, StatusIcon: Battery4BarIcon, color: 'success', description: 'Battery is OK' },
    { max: Number.MAX_VALUE, level: 4, StatusIcon: BatteryFullIcon, color: 'success', description: 'Battery is full' },
] as const;

export type ScoutStatusInfo = {
    timestamp: ScoutLatestData['timestamp'];
    status: ScoutLatestData['status'];
    battery: ScoutLatestData['battery'];
};

export type ScoutBatteryStatus = {
    max: number;
    /**
     * 0-4 empty-full
     */
    level: number;
    StatusIcon: typeof SvgIcon;
    color: (typeof BATTERY_STATUSES)[number]['color'] | 'error';
    description: string;
    timestamp?: string;
};

type ScoutBatteryStatusBase = Omit<ScoutBatteryStatus, 'timestamp'>;

const UNKNOWN_BATTERY_STATUS: ScoutBatteryStatusBase = {
    max: 0,
    level: 0,
    StatusIcon: BatteryUnknownIcon,
    color: 'error',
    description: 'Battery status is unknown',
};

export const getScoutBatteryStatus = (statusInfo?: ScoutStatusInfo | null): ScoutBatteryStatus | null => {
    if (!statusInfo) {
        return null;
    }

    const timestamp = statusInfo.timestamp ?? undefined;
    const batteryPercentage = statusInfo.battery?.soc_percent;

    if (batteryPercentage == null || Number.isNaN(batteryPercentage)) {
        return {
            ...UNKNOWN_BATTERY_STATUS,
            timestamp,
        } satisfies ScoutBatteryStatus;
    }

    const clampedPercentage = Math.max(0, Math.min(100, batteryPercentage));
    const batteryStatus = BATTERY_STATUSES.find(({ max }) => clampedPercentage <= max) ?? BATTERY_STATUSES[0];

    return {
        ...batteryStatus,
        timestamp,
    };
};

/**
 * Mission state derived from a ScoutLatestData payload as defined in Tether scout-data docs:
 *   - 'no-mission'         : mission == null (no mission loaded, or last one finished/archived)
 *   - 'loaded-not-started' : mission != null AND current_waypoint in {0, null}
 *   - 'executing'          : status.mode == 'auto' AND current_waypoint >= 1
 *   - 'manual'             : status.mode == 'manual' (pilot has direct control)
 */
export type ScoutMissionState =
    | { kind: 'no-mission' }
    | { kind: 'loaded-not-started'; waypoints: ScoutLatestDataWaypoint[] }
    | {
          kind: 'executing';
          current: ScoutLatestDataWaypoint;
          remaining: ScoutLatestDataWaypoint[];
          waypoints: ScoutLatestDataWaypoint[];
          isFinalWaypoint: boolean;
      }
    | {
          kind: 'manual';
          mission: { current: ScoutLatestDataWaypoint | null; waypoints: ScoutLatestDataWaypoint[] } | null;
      };

const sortedWaypoints = (waypoints: ScoutLatestDataWaypoint[]): ScoutLatestDataWaypoint[] =>
    sortBy(waypoints, 'sequence');

const findWaypointBySequence = (
    waypoints: ScoutLatestDataWaypoint[],
    sequence: number
): ScoutLatestDataWaypoint | null => find(waypoints, { sequence }) ?? null;

export const getScoutMissionState = (scoutData?: ScoutLatestData): ScoutMissionState | null => {
    if (!scoutData) {
        return null;
    }

    const mode = scoutData.status?.mode ?? null;
    const mission = scoutData.mission;

    if (mode === 'manual') {
        if (!mission) {
            return { kind: 'manual', mission: null };
        }
        const waypoints = sortedWaypoints(mission.waypoints);
        const currentSequence = mission.current_waypoint;
        const current =
            currentSequence != null && currentSequence >= 1 ? findWaypointBySequence(waypoints, currentSequence) : null;
        return { kind: 'manual', mission: { current, waypoints } };
    }

    if (!mission) {
        return { kind: 'no-mission' };
    }

    const waypoints = sortedWaypoints(mission.waypoints);
    const currentSequence = mission.current_waypoint;

    // Per the docs, mission loaded but not yet started: current_waypoint is 0 or null.
    // We also treat "auto with currentSequence >= 1 but no matching waypoint" as
    // not-started rather than fabricating an executing state.
    if (mode === 'auto' && currentSequence != null && currentSequence >= 1) {
        const current = findWaypointBySequence(waypoints, currentSequence);
        if (current) {
            const lastSequence = waypoints.at(-1)?.sequence ?? current.sequence;
            const remaining = waypoints.filter((wp) => wp.sequence >= current.sequence);
            return {
                kind: 'executing',
                current,
                remaining,
                waypoints,
                isFinalWaypoint: current.sequence === lastSequence,
            };
        } else {
            console.warn(
                `Scout mission: current_waypoint ${currentSequence} not found in waypoints. Treating as loaded-not-started.`
            );
            mixpanel.track('scout_waypoint_mismatch', {
                scoutId: scoutData.scoutId,
                currentSequence,
                waypointCount: waypoints.length,
            });
        }
    }

    return { kind: 'loaded-not-started', waypoints };
};

/**
 * Display label for the scout's current activity.
 *
 * Prefers `status.description` (a pre-formatted, display-ready string from the device)
 * and enriches it with mission context where the API string alone would be ambiguous
 * (e.g. "Manual control" + whether a mission is loaded). Final-waypoint drift is
 * surfaced explicitly because the API has no native concept of it.
 */
export const getScoutDisplayLabel = (scoutData?: ScoutLatestData): string | null => {
    if (!scoutData) {
        return null;
    }

    const description = scoutData.status?.description ?? null;
    const state = getScoutMissionState(scoutData);
    if (!state) {
        return null;
    }

    switch (state.kind) {
        case 'manual': {
            const base = description ?? 'Manual control';
            const missionLabel = state.mission != null ? 'Mission set' : 'No mission';
            return `${base} - ${missionLabel}`;
        }
        case 'executing':
            return state.isFinalWaypoint
                ? 'Mission complete - drifting'
                : (description ?? `Heading to Waypoint ${state.current.sequence}`);
        case 'loaded-not-started':
            return 'Mission set but not started';
        case 'no-mission':
            return description ?? 'No mission';
        default: {
            const _exhaustive: never = state;
            return _exhaustive;
        }
    }
};

/**
 * Waypoints the map should render for a scout: the active route under any state
 * where a mission is loaded (executing / loaded-not-started / manual-with-mission),
 * empty otherwise.
 */
export const getScoutDisplayWaypoints = (scoutData?: ScoutLatestData): ScoutLatestDataWaypoint[] => {
    const state = getScoutMissionState(scoutData);
    if (!state) {
        return [];
    }
    switch (state.kind) {
        case 'executing':
            return state.remaining;
        case 'loaded-not-started':
            return state.waypoints;
        case 'manual':
            return state.mission?.waypoints ?? [];
        case 'no-mission':
            return [];
        default: {
            const _exhaustive: never = state;
            return _exhaustive;
        }
    }
};
